import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Avatar as MuiAvatar } from '@mui/material';
import { RED } from '../../utils/brandColors';
import { DOMAIN } from '../../redux/constants';
import { getBrandedAvatarDataUrl } from '../../utils/avatarGenerator';

// Avatar — primitive.
// Sizes: xs (16px), sm (24px), md (36px), lg (48px).
// Initials fallback when no src; bg = RED for visual brand consistency.
//
// Responsive: Pattern 1 (internal). Sizes are explicit; no breakpoint logic.
//
// FALLBACK CHAIN: real image → branded avatar → initials, advanced by the
// img's own error event. A DB avatar path can dangle (the file 404s while
// the row still points at it - seen on prod, 2026-08-05); it must degrade to
// the SAME branded avatar a never-set avatar gets, so a lost file and no
// file are indistinguishable.
//
// This deliberately does NOT use useImageRetry. MUI Avatar runs its own
// image probe and swaps to `children` the moment that probe errors -
// unmounting our <img> and with it the retry hook's error signal, so the
// hook's `failed` state could never be reached in a real browser (verified
// against a live 404 during the 2026-08-05 QA pass: the retry chain died
// after one attempt and the branded swap never fired). An instant
// error → next-source advance has no timer to strand and re-mounts the img
// via the src change.

const SIZE_PX = { xs: 16, sm: 24, md: 36, lg: 48 };
const FONT_PX = { xs: '0.55rem', sm: '0.7rem', md: '0.85rem', lg: '1rem' };

function getInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const second = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + second).toUpperCase() || '?';
}

export default function Avatar({ src, name = '', size = 'md', sx }) {
  const px = SIZE_PX[size];
  const fontSize = FONT_PX[size];

  const isDefaultAvatar = src && src.includes('default_avatar_');
  const isNoAvatar = !src;

  let resolvedSrc;

  if (isDefaultAvatar) {
    const match = src.match(/default_avatar_(\d+)/);
    const index = match ? parseInt(match[1], 10) : 0;
    resolvedSrc = getBrandedAvatarDataUrl(index);
  } else if (isNoAvatar) {
    if (process.env.NODE_ENV !== 'test') {
      resolvedSrc = getBrandedAvatarDataUrl(name || '?');
    }
  } else {
    resolvedSrc = src && !src.startsWith('http') && !src.startsWith('data:')
      ? `${DOMAIN}${src}`
      : src;
  }

  // Ordered fallback sources. The branded avatar is appended only when it is
  // not already what we are showing (default_avatar_ and no-avatar cases
  // resolve to it directly - re-trying it on failure would be a loop).
  const branded = getBrandedAvatarDataUrl(name || '?');
  const sources = [];
  if (resolvedSrc) sources.push(resolvedSrc);
  if (resolvedSrc && resolvedSrc !== branded && !resolvedSrc.startsWith('/avatars/')) {
    sources.push(branded);
  }

  const [stage, setStage] = useState(0);
  // A new identity (avatar change, list row recycle) restarts the chain.
  useEffect(() => { setStage(0); }, [resolvedSrc]);

  const finalSrc = stage < sources.length ? sources[stage] : undefined;

  return (
    <MuiAvatar
      src={finalSrc}
      alt={name}
      imgProps={{
        onError: () => setStage((s) => s + 1),
        decoding: 'async',
        // Defer offscreen avatar fetches. Matters most in the Settings
        // avatar picker: 24 tiles resolving to ~500KB PNGs each (~13MB)
        // would otherwise all load eagerly.
        loading: 'lazy',
      }}
      sx={{
        width: px,
        height: px,
        fontSize,
        fontWeight: 700,
        bgcolor: RED,
        color: '#fff',
        ...sx,
      }}
    >
      {getInitials(name)}
    </MuiAvatar>
  );
}

Avatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  sx: PropTypes.object,
};
